import { apiFetch } from './http';

// ─────────────────────────────────────────────────────────────────────
// «AVBESTILL OG REFUNDER» — staff cancel a paid booking (#284, client half).
//
// `POST /organization/bookings/:id/refund`, by the booking's LEGACY id (the
// calendar's id space, as for every staff booking route). Nothing else is
// sent: the server decides the amount — the whole payment, court and equipment
// — and re-reads the booking under its lock. The answer is a CODE; the screen
// decides the sentence (CLAUDE.md, «API and domain error CODES are never
// translated»).
//
// A 403 `reauthentication_required` (the refund wants a sign-in from the last
// fifteen minutes) is ALSO reported by the transport, which opens the step-up
// prompt (`lib/api/http.ts`); the caller shows the code and the person presses
// again once they have confirmed — the same as the payment-account screen.
//
// ── WHO SEES THE BUTTON ──────────────────────────────────────────────
//
// `payment.refund`, read from the same `/me` capability union the other doors
// read (`bookingOverride.ts`, `messagingAccess.ts`). This one is a DOOR, so an
// unknown union is «no»: drawing a button the server refuses is the thing doors
// exist to avoid. (`bookingOverride` defaults the other way because it is a
// flag on a request, not a control.) FOR DRAWING, NEVER FOR DECIDING — the
// server guards the route regardless.
// ─────────────────────────────────────────────────────────────────────

/** Spelled exactly as the API's catalog has it — a test reads both. */
export const PAYMENT_REFUND = 'payment.refund';

export interface RefundRightInputs {
  /** The capability union `/me` reports for the current scope; null = unknown. */
  capabilities: readonly string[] | null;
  /** Whether the browser talks to the API at all (`isApiMode()`). */
  apiMode: boolean;
  /** StarPoint operating AS the organization under an audited context (#976). */
  operatingAs: boolean;
}

/**
 * May this session be offered «Avbestill og refunder»?
 *
 *   • Off the API there is no payment and no route: no.
 *   • An impersonation context holds every organization-derived capability of
 *     the one organization (`payment.refund` is one), and `/me` reports no
 *     membership for it.
 *   • Otherwise the union decides, and unknown (null) is no.
 */
export function refundRight(input: RefundRightInputs): boolean {
  if (!input.apiMode) return false;
  if (input.operatingAs) return true;
  return input.capabilities?.includes(PAYMENT_REFUND) ?? false;
}

/**
 * What the calendar is handed (#284): whether this session is offered the
 * refund, and what the shell does once the server has refunded — the booking
 * is gone (deleted with the refund), so the calendar re-reads.
 */
export interface StaffRefundDoor {
  /** `refundRight()` — the session holds `payment.refund` here, or operates as the organization. */
  readonly allowed: boolean;
  /** The server cancelled and refunded `bookingId` (a legacy id). */
  readonly onRefunded: (bookingId: string) => Promise<void>;
}

export type StaffRefundResult =
  /** Cancelled and refunded. The booking is gone from the calendar. */
  | { readonly state: 'refunded'; readonly refundMinor: number; readonly currency: string }
  /**
   * 202: the provider has not confirmed the refund. The court is STILL HELD and
   * reconciliation settles it — the screen must not say «avbestilt».
   */
  | { readonly state: 'unconfirmed' }
  /** Refused, with the server's code (`not_paid`, `refund_failed`, …). */
  | { readonly state: 'refused'; readonly code: string }
  /** No answer, or one without a code. Nothing is known. */
  | { readonly state: 'error'; readonly status: number };

export async function refundStaffBooking(legacyId: string): Promise<StaffRefundResult> {
  let res: Response;
  try {
    res = await apiFetch(`/organization/bookings/${encodeURIComponent(legacyId)}/refund`, {
      method: 'POST',
    });
  } catch {
    return { state: 'error', status: 0 };
  }
  const body = (await res.json().catch(() => null)) as Record<string, unknown> | null;
  if (res.status === 200 && body?.cancelled === true) {
    return {
      state: 'refunded',
      refundMinor: typeof body.refundMinor === 'number' ? body.refundMinor : 0,
      currency: typeof body.currency === 'string' ? body.currency : 'NOK',
    };
  }
  if (res.status === 202) return { state: 'unconfirmed' };
  if (typeof body?.error === 'string') return { state: 'refused', code: body.error };
  return { state: 'error', status: res.status };
}

/** What `GET /bookings` says a paid booking was paid (#284), or null. Display only. */
export function paidAmountOf(booking: unknown): { amountMinor: number; currency: string } | null {
  const v = (booking as { paidAmount?: unknown } | null)?.paidAmount as
    { amountMinor?: unknown; currency?: unknown } | undefined;
  if (!v || typeof v.amountMinor !== 'number' || typeof v.currency !== 'string') return null;
  return { amountMinor: v.amountMinor, currency: v.currency };
}
